import { Component } from "react";
import { G_DARK, globalStyles } from "./Shared";

/**
 * Turns an uncaught render error into something a person can act on.
 *
 * React's default is to unmount the whole tree, which renders as a completely
 * blank page — no message, no hint, nothing to report beyond "it's white". That
 * is exactly what a single mistyped prop produced on the Settings tab on
 * 2026-08-12: `<Select>` was given children instead of its `options` prop, the
 * internal `options.map()` threw, and the entire portal disappeared.
 *
 * The specific bug is fixed and tested. This exists because the *next* one will
 * be different, and a blank screen is the least diagnosable failure a user can
 * be shown. Class component on purpose: componentDidCatch has no hook
 * equivalent, and this is the one place React still requires a class.
 */
export default class ErrorBoundary extends Component {
  state = { error: null };

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Keep the stack in the console for whoever is asked to look. The message
    // on screen stays short; this is where the detail lives.
    console.error("Unhandled render error:", error, info?.componentStack);
  }

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div role="alert" style={{
        maxWidth: 640, margin: "80px auto", padding: "28px 32px",
        fontFamily: "'DM Sans', system-ui, sans-serif",
        border: "1px solid rgba(220,38,38,0.3)", borderRadius: 12,
        background: "rgba(220,38,38,0.04)",
      }}>
        {/* This fallback replaces the whole tree, Layout's <style> with it, so
            the variables G_DARK names would be undefined and "Reload the page"
            white on nothing. */}
        <style>{globalStyles}</style>
        <h1 style={{ margin: "0 0 10px", fontSize: 20, fontWeight: 700, color: "#b91c1c" }}>
          This page failed to load
        </h1>
        <p style={{ margin: "0 0 16px", fontSize: 14, color: "#374151", lineHeight: 1.5 }}>
          Something went wrong while displaying this page. Your data has not been affected —
          this is a fault in the portal's interface, not in anything you submitted.
        </p>
        {/* The message, not the stack: enough to quote in a bug report without
            putting a wall of frames in front of a researcher. */}
        <pre style={{
          margin: "0 0 18px", padding: "10px 12px", borderRadius: 8,
          background: "#fff", border: "1px solid #e5e7eb",
          fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: "#b91c1c",
          whiteSpace: "pre-wrap", wordBreak: "break-word",
        }}>
          {String(this.state.error?.message || this.state.error)}
        </pre>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button
            onClick={() => window.location.reload()}
            style={{
              background: G_DARK, color: "#fff", border: "none", borderRadius: 8,
              padding: "10px 18px", fontSize: 14, fontWeight: 600, cursor: "pointer",
              fontFamily: "inherit", minHeight: 44,
            }}
          >
            Reload the page
          </button>
          <button
            onClick={() => { window.location.href = "/projects"; }}
            style={{
              background: "#fff", color: "#374151", border: "1px solid #d1d5db",
              borderRadius: 8, padding: "10px 18px", fontSize: 14, fontWeight: 500,
              cursor: "pointer", fontFamily: "inherit", minHeight: 44,
            }}
          >
            Back to projects
          </button>
        </div>
        <p style={{ margin: "16px 0 0", fontSize: 12, color: "#6b7280" }}>
          If it keeps happening, send this message to the facility along with what you were doing.
        </p>
      </div>
    );
  }
}
